import { lazy, Suspense, useEffect, useState } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { selectAuthBootstrapComplete } from '../../store/features/auth';

const IDLE_FALLBACK_MS = 2500;

const GoogleOneTapPrompt = lazy(() =>
  import('./GoogleOneTapPrompt').then((module) => ({
    default: module.GoogleOneTapPrompt,
  })),
);

function scheduleAfterIdle(callback: () => void): () => void {
  if (typeof requestIdleCallback !== 'undefined') {
    const id = requestIdleCallback(callback, { timeout: 4000 });
    return () => cancelIdleCallback(id);
  }
  const timer = window.setTimeout(callback, IDLE_FALLBACK_MS);
  return () => window.clearTimeout(timer);
}

/**
 * Loads Google One Tap after auth bootstrap and browser idle — not on first paint.
 */
export function DeferredGoogleOneTapMount() {
  const bootstrapComplete = useAppSelector(selectAuthBootstrapComplete);
  const [mountReady, setMountReady] = useState(false);

  useEffect(() => {
    if (!bootstrapComplete) {
      setMountReady(false);
      return;
    }

    return scheduleAfterIdle(() => {
      setMountReady(true);
    });
  }, [bootstrapComplete]);

  if (!mountReady) return null;

  return (
    <Suspense fallback={null}>
      <GoogleOneTapPrompt />
    </Suspense>
  );
}
